import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "./AdminShared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Star } from "lucide-react";

function useContent(kind) {
  const qc = useQueryClient();
  const key = [`admin-${kind}`];
  const { data = [] } = useQuery({ queryKey: key, queryFn: async () => (await api.get(`/${kind}?include_disabled=true`)).data });
  const invalidate = () => { qc.invalidateQueries({ queryKey: key }); qc.invalidateQueries({ queryKey: [kind] }); };
  const save = useMutation({
    mutationFn: async ({ id, payload }) => id ? (await api.put(`/admin/${kind}/${id}`, payload)).data : (await api.post(`/admin/${kind}`, payload)).data,
    onSuccess: () => { toast.success("Saved"); invalidate(); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const remove = useMutation({
    mutationFn: async (id) => api.delete(`/admin/${kind}/${id}`),
    onSuccess: () => { toast.success("Deleted"); invalidate(); },
  });
  return { data, save, remove };
}

function Testimonials() {
  const { data, save, remove } = useContent("testimonials");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const edit = (t) => { setForm(t || { name: "", role: "", rating: 5, message: "", enabled: true }); setOpen(true); };
  return (
    <div>
      <div className="flex justify-end mb-4"><Button data-testid="add-testimonial-btn" onClick={() => edit(null)} className="bg-gradient-to-r from-purple-600 to-blue-600 text-white border-0"><Plus className="w-4 h-4 mr-1" /> Add</Button></div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {data.map((t) => (
          <div key={t.id} data-testid={`admin-testimonial-${t.id}`} className={`glass rounded-xl p-5 ${!t.enabled ? "opacity-60" : ""}`}>
            <div className="flex justify-between items-start">
              <div className="flex gap-1 mb-2">{Array.from({ length: t.rating }).map((_, i) => <Star key={i} className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400" />)}</div>
              <div className="flex gap-1">
                <Button size="icon" variant="ghost" onClick={() => edit(t)} className="h-7 w-7 text-slate-300"><Pencil className="w-3.5 h-3.5" /></Button>
                <Button size="icon" variant="ghost" onClick={() => remove.mutate(t.id)} className="h-7 w-7 text-red-400"><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            </div>
            <p className="text-sm text-slate-300">“{t.message}”</p>
            <div className="text-xs text-slate-500 mt-2">{t.name} · {t.role}</div>
          </div>
        ))}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0b0c12] border-white/10 text-white">
          <DialogHeader><DialogTitle className="font-heading">{form.id ? "Edit" : "New"} Testimonial</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-slate-300">Name</Label><Input data-testid="testimonial-name" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 bg-white/5 border-white/10 text-white" /></div>
              <div><Label className="text-slate-300">Role</Label><Input value={form.role || ""} onChange={(e) => setForm({ ...form, role: e.target.value })} className="mt-1 bg-white/5 border-white/10 text-white" /></div>
            </div>
            <div><Label className="text-slate-300">Rating (1-5)</Label><Input type="number" min={1} max={5} value={form.rating || 5} onChange={(e) => setForm({ ...form, rating: Number(e.target.value) })} className="mt-1 bg-white/5 border-white/10 text-white" /></div>
            <div><Label className="text-slate-300">Message</Label><Textarea data-testid="testimonial-message" value={form.message || ""} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1 bg-white/5 border-white/10 text-white" /></div>
            <label className="flex items-center gap-2 text-sm text-slate-300"><Switch checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} /> Enabled</label>
          </div>
          <DialogFooter><Button data-testid="testimonial-save" onClick={() => save.mutate({ id: form.id, payload: { name: form.name, role: form.role, rating: Number(form.rating), message: form.message, enabled: form.enabled !== false } }, { onSuccess: () => setOpen(false) })} className="bg-gradient-to-r from-purple-600 to-blue-600 text-white border-0">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Faqs() {
  const { data, save, remove } = useContent("faqs");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const edit = (f) => { setForm(f || { question: "", answer: "", enabled: true, order: 0 }); setOpen(true); };
  return (
    <div>
      <div className="flex justify-end mb-4"><Button data-testid="add-faq-btn" onClick={() => edit(null)} className="bg-gradient-to-r from-purple-600 to-blue-600 text-white border-0"><Plus className="w-4 h-4 mr-1" /> Add</Button></div>
      <div className="space-y-3">
        {data.map((f) => (
          <div key={f.id} data-testid={`admin-faq-${f.id}`} className={`glass rounded-xl p-4 flex justify-between items-start gap-4 ${!f.enabled ? "opacity-60" : ""}`}>
            <div><div className="text-white font-medium text-sm">{f.question}</div><div className="text-slate-400 text-sm mt-1">{f.answer}</div></div>
            <div className="flex gap-1 shrink-0">
              <Button size="icon" variant="ghost" onClick={() => edit(f)} className="h-7 w-7 text-slate-300"><Pencil className="w-3.5 h-3.5" /></Button>
              <Button size="icon" variant="ghost" onClick={() => remove.mutate(f.id)} className="h-7 w-7 text-red-400"><Trash2 className="w-3.5 h-3.5" /></Button>
            </div>
          </div>
        ))}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0b0c12] border-white/10 text-white">
          <DialogHeader><DialogTitle className="font-heading">{form.id ? "Edit" : "New"} FAQ</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-slate-300">Question</Label><Input data-testid="faq-question" value={form.question || ""} onChange={(e) => setForm({ ...form, question: e.target.value })} className="mt-1 bg-white/5 border-white/10 text-white" /></div>
            <div><Label className="text-slate-300">Answer</Label><Textarea data-testid="faq-answer" value={form.answer || ""} onChange={(e) => setForm({ ...form, answer: e.target.value })} className="mt-1 bg-white/5 border-white/10 text-white" /></div>
            <div className="flex items-center gap-6">
              <div className="flex-1"><Label className="text-slate-300">Order</Label><Input type="number" value={form.order || 0} onChange={(e) => setForm({ ...form, order: Number(e.target.value) })} className="mt-1 bg-white/5 border-white/10 text-white" /></div>
              <label className="flex items-center gap-2 text-sm text-slate-300 mt-5"><Switch checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} /> Enabled</label>
            </div>
          </div>
          <DialogFooter><Button data-testid="faq-save" onClick={() => save.mutate({ id: form.id, payload: { question: form.question, answer: form.answer, order: Number(form.order || 0), enabled: form.enabled !== false } }, { onSuccess: () => setOpen(false) })} className="bg-gradient-to-r from-purple-600 to-blue-600 text-white border-0">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Banners() {
  const { data, save, remove } = useContent("banners");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const edit = (b) => { setForm(b || { title: "", subtitle: "", image_url: "", cta_text: "", cta_link: "", enabled: true }); setOpen(true); };
  return (
    <div>
      <div className="flex justify-end mb-4"><Button data-testid="add-banner-btn" onClick={() => edit(null)} className="bg-gradient-to-r from-purple-600 to-blue-600 text-white border-0"><Plus className="w-4 h-4 mr-1" /> Add</Button></div>
      <div className="space-y-3">
        {data.length === 0 && <div className="glass rounded-xl p-8 text-center text-slate-500">No promo banners yet. Add one to highlight offers on the homepage.</div>}
        {data.map((b) => (
          <div key={b.id} data-testid={`admin-banner-${b.id}`} className={`glass rounded-xl p-4 flex justify-between items-start gap-4 ${!b.enabled ? "opacity-60" : ""}`}>
            <div><div className="text-white font-medium">{b.title}</div><div className="text-slate-400 text-sm">{b.subtitle}</div></div>
            <div className="flex gap-1 shrink-0">
              <Button size="icon" variant="ghost" onClick={() => edit(b)} className="h-7 w-7 text-slate-300"><Pencil className="w-3.5 h-3.5" /></Button>
              <Button size="icon" variant="ghost" onClick={() => remove.mutate(b.id)} className="h-7 w-7 text-red-400"><Trash2 className="w-3.5 h-3.5" /></Button>
            </div>
          </div>
        ))}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0b0c12] border-white/10 text-white">
          <DialogHeader><DialogTitle className="font-heading">{form.id ? "Edit" : "New"} Banner</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-slate-300">Title</Label><Input data-testid="banner-title" value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1 bg-white/5 border-white/10 text-white" /></div>
            <div><Label className="text-slate-300">Subtitle</Label><Input value={form.subtitle || ""} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} className="mt-1 bg-white/5 border-white/10 text-white" /></div>
            <div><Label className="text-slate-300">Image URL</Label><Input value={form.image_url || ""} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className="mt-1 bg-white/5 border-white/10 text-white" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-slate-300">CTA Text</Label><Input value={form.cta_text || ""} onChange={(e) => setForm({ ...form, cta_text: e.target.value })} className="mt-1 bg-white/5 border-white/10 text-white" /></div>
              <div><Label className="text-slate-300">CTA Link</Label><Input value={form.cta_link || ""} onChange={(e) => setForm({ ...form, cta_link: e.target.value })} className="mt-1 bg-white/5 border-white/10 text-white" /></div>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-300"><Switch checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} /> Enabled</label>
          </div>
          <DialogFooter><Button data-testid="banner-save" onClick={() => save.mutate({ id: form.id, payload: { title: form.title, subtitle: form.subtitle, image_url: form.image_url, cta_text: form.cta_text, cta_link: form.cta_link, enabled: form.enabled !== false } }, { onSuccess: () => setOpen(false) })} className="bg-gradient-to-r from-purple-600 to-blue-600 text-white border-0">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function AdminContent() {
  return (
    <div>
      <PageHeader title="Website Content" subtitle="Manage testimonials, FAQs and promo banners" />
      <Tabs defaultValue="testimonials">
        <TabsList className="bg-white/5 border border-white/10">
          <TabsTrigger value="testimonials" data-testid="content-tab-testimonials">Testimonials</TabsTrigger>
          <TabsTrigger value="faqs" data-testid="content-tab-faqs">FAQs</TabsTrigger>
          <TabsTrigger value="banners" data-testid="content-tab-banners">Banners</TabsTrigger>
        </TabsList>
        <TabsContent value="testimonials" className="mt-6"><Testimonials /></TabsContent>
        <TabsContent value="faqs" className="mt-6"><Faqs /></TabsContent>
        <TabsContent value="banners" className="mt-6"><Banners /></TabsContent>
      </Tabs>
    </div>
  );
}
